import {Grid} from '@mui/material';
import * as React from 'react';
import {useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import CustomisedButton from '../../components/button/button';
import CustomisedDropDown from '../../components/dropDown/dropDown';
import CustomisedSlider from '../../components/slider/slider';
import {UUID} from '../../config/cacheKey';
import {setHeaderTitle} from '../../redux/headerTitle/headerTitleSlice';
import './createNewRequest.css';
import CreateNewRequestSkeleton from './createNewRequestSkeleton';
import {useMutation, useQuery, useQueryClient} from 'react-query';
import {v4 as uuid} from 'uuid';
import {SupplierSelect} from '../../components/selector/SupplierSelect';
import {useAuth} from '../../config/AuthProvider/authProvider';
import {
    IGetLoanTemplatesResponse,
    IRegisterLoanResponse,
} from '../../models/loan.model';
import {
    ICalculateLoanParamsRequest,
    ICalculateLoanParamsResponse,
    IDropDownResponse,
    IGetRegistrationDurationRequest,
    IInitializeParamsResponse,
    IInitializeRegistrationParamsResponse,
    ILoanTemplateDetailResponse,
} from '../../models/registration.model';
import {
    changeRouteType,
    getpervInnerState,
} from '../../redux/handleBackBtn/handleBackBtnSlice';
import {setUnAuthorizedState} from '../../redux/unAuthorizedState/unAuthorizedStateSlice';
import {getJwtToken, getTokenTime} from '../../services/auth.service';
import {GetLoanTemplates, registerLoan} from '../../services/loan.service';
import {
    calculateLoanParams,
    getRegistrationDuration,
    initializeRegistrationParams,
} from '../../services/registration.service';
import {IContainerApp} from '../navigationButtonComponents/loansTab/containerApp/IContainerApp';
import TemplateLoanCard from '../../components/card/loanCard/TemplateLoanCard';

// ─── Types ───────────────────────────────────────────────────────────────────

interface StepState {
    // Step 1 – data loaded once from the API
    loanTemplates: IGetLoanTemplatesResponse[];
    initializeParams: IInitializeParamsResponse[];

    // Step 2 – organisation (optional, depends on selected loan template)
    organizations: IDropDownResponse[];
    selectedOrganization: IDropDownResponse | undefined;

    // Step 3 – financial supplier
    loanTemplateDetail: ILoanTemplateDetailResponse[];
    selectedLoanTemplateDetailItem: ILoanTemplateDetailResponse | undefined;

    // Step 4 – loan amount (slider)
    loanAmount: number;

    // Step 5 – refund / repayment period
    refundPeriod: IDropDownResponse[];
    selectedRefundPeriod: IDropDownResponse | undefined;

    // Step 6 – calculated installment (read-only preview)
    installmentCalculated: ICalculateLoanParamsResponse | undefined;
}

const INITIAL_STATE: StepState = {
    loanTemplates: [],
    initializeParams: [],
    organizations: [],
    selectedOrganization: undefined,
    loanTemplateDetail: [],
    selectedLoanTemplateDetailItem: undefined,
    loanAmount: 0,
    refundPeriod: [],
    selectedRefundPeriod: undefined,
    installmentCalculated: undefined,
};

// ─── Helper – build the ILoanTemplateDetailResponse list from financial suppliers ─

function buildSupplierList(
    data: IInitializeRegistrationParamsResponse
): ILoanTemplateDetailResponse[] {
    return data.financialSuppliers.map((s) => ({
        financialSupplierTitle: s.title,
        financialSupplierId: s.id,
        financialSupplier: s.additionalData,
        amountStep: 0,
        maxAmount: 0,
        minAmount: 0,
        logoUrl: '',
    }));
}

// ─── Component ───────────────────────────────────────────────────────────────

const CreateNewRequest = ({setStep, loanTemplateKey}: IContainerApp) => {
    const {setRefKey} = useAuth();
    const dispatch = useDispatch();
    const queryClient = useQueryClient();

    // Resolve the loan template key once so it never changes mid-render
    const resolvedLoanTemplateKey: string =
        loanTemplateKey ??
        String(queryClient.getQueryData('LOAN_TEMPLATE_KEY'));

    // All sequential state lives in one object so relationships are explicit
    const [state, setState] = useState<StepState>(INITIAL_STATE);

    // ── Bootstrap effects ──────────────────────────────────────────────────

    useEffect(() => {
        dispatch(
            setHeaderTitle({
                header: 'مشخصات تسهیلات',
                color: 'var(--header-color)',
                parentRoute: '',
            })
        );
        dispatch(changeRouteType({routeType: 'child'}));
        dispatch(
            getpervInnerState({
                prevInnerState: `confirmLoanTemplate ${Math.random()}`,
            })
        );

        // Fetch the card-level loan template data (title, description, logo)
        GetLoanTemplatesMutate({loanTemplateKey: resolvedLoanTemplateKey});
    }, []);

    // ── Mutations ─────────────────────────────────────────────────────────

    const {mutate: GetLoanTemplatesMutate} = useMutation(GetLoanTemplates, {
        onSuccess: (data: IGetLoanTemplatesResponse[]) => {
            if (data) {
                setState((prev) => ({...prev, loanTemplates: data}));
            }
        },
    });

    /**
     * Step 5 → fetch available repayment period options.
     * Triggered when loanAmount OR selectedLoanTemplateDetailItem changes
     * (see handleLoanAmountChange).
     */
    const getRegistrationDurationMutation = useMutation(
        getRegistrationDuration,
        {
            onSuccess: (data: IDropDownResponse[]) => {
                setState((prev) => ({
                    ...prev,
                    refundPeriod: data ?? [],
                    // Reset downstream steps whenever the period list refreshes
                    selectedRefundPeriod: undefined,
                    installmentCalculated: undefined,
                }));
            },
        }
    );

    /**
     * Step 6 → calculate installment preview.
     * Triggered when selectedRefundPeriod changes (see handleRefundPeriodSelect).
     */
    const calculateLoanParamsMutation = useMutation(calculateLoanParams, {
        onSuccess: (data: ICalculateLoanParamsResponse) => {
            if (data) {
                setState((prev) => ({...prev, installmentCalculated: data}));
            }
        },
    });

    const registerLoanMutation = useMutation(registerLoan, {
        onSuccess: (data: IRegisterLoanResponse) => {
            if (data) {
                setRefKey(data.referenceKey + '');
                setStep(uuid());
            }
        },
    });

    // ── Main data load (Step 1) ────────────────────────────────────────────

    const {isLoading} = useQuery<IInitializeRegistrationParamsResponse>(
        'initializeRegistrationParams',
        (): Promise<IInitializeRegistrationParamsResponse> =>
            initializeRegistrationParams({
                loanTemplateKey: resolvedLoanTemplateKey,
            }),
        {
            onSuccess: (data) => {
                if (!data?.initializeParams) return;

                // Build the loan template dropdown from initializeParams
                const loanTemplateOptions: IDropDownResponse[] =
                    data.initializeParams.map((p) => p.loanTemplate);

                // Default to the first loan template
                const defaultLoanTemplate =
                    data.initializeParams[0].loanTemplate;
                const defaultInitParam = data.initializeParams[0];

                // Determine organisations for the default template
                const defaultOrganizations: IDropDownResponse[] =
                    defaultInitParam.organizations ?? [];

                // Supplier list (detail) for the default template
                const defaultLoanTemplateDetail: ILoanTemplateDetailResponse[] =
                    defaultInitParam.loanTemplateDetail.length > 0
                        ? defaultInitParam.loanTemplateDetail
                        : buildSupplierList(data);

                // If there are no organisations the supplier & slider unlock immediately
                const noOrganizationRequired =
                    defaultOrganizations.length === 0;
                const defaultSupplier = noOrganizationRequired
                    ? defaultLoanTemplateDetail[0]
                    : undefined;

                setState((prev) => ({
                    ...prev,
                    initializeParams: data.initializeParams,
                    organizations: defaultOrganizations,
                    loanTemplateDetail: defaultLoanTemplateDetail,
                    // Auto-select supplier only when there is no organisation gate
                    selectedLoanTemplateDetailItem: defaultSupplier,
                    loanAmount: defaultSupplier?.minAmount ?? 0,
                }));

                // If a supplier was auto-selected we can already fetch period options
                if (defaultSupplier) {
                    getRegistrationDurationMutation.mutate({
                        loanAmount: defaultSupplier.minAmount,
                        loanTemplateId: defaultLoanTemplate.id,
                        financialSupplierId:
                            defaultSupplier.financialSupplierId,
                    } as IGetRegistrationDurationRequest);
                }
            },
        }
    );

    // ── Sequential step handlers ───────────────────────────────────────────

    /**
     * Step 2 – user selects (or changes) organisation.
     * Resets everything downstream: supplier, slider, period, installment.
     */
    const handleOrganizationSelect = (org: IDropDownResponse) => {
        // Find the initializeParam for the currently active loan template
        const currentLoanTemplateId = state.initializeParams.find((p) =>
            p.organizations?.some((o) => o.id === org.id)
        )?.loanTemplate.id;

        const matchingParam = state.initializeParams.find(
            (p) => p.loanTemplate.id === currentLoanTemplateId
        );

        const detail: ILoanTemplateDetailResponse[] =
            matchingParam?.loanTemplateDetail ?? state.loanTemplateDetail;

        // Auto-select the first supplier now that the org gate is passed
        const firstSupplier = detail[0];

        setState((prev) => ({
            ...prev,
            selectedOrganization: org,
            loanTemplateDetail: detail,
            selectedLoanTemplateDetailItem: firstSupplier,
            loanAmount: firstSupplier?.minAmount ?? 0,
            refundPeriod: [],
            selectedRefundPeriod: undefined,
            installmentCalculated: undefined,
        }));

        if (firstSupplier) {
            getRegistrationDurationMutation.mutate({
                loanAmount: firstSupplier.minAmount,
                loanTemplateId: currentLoanTemplateId,
                financialSupplierId: firstSupplier.financialSupplierId,
            } as IGetRegistrationDurationRequest);
        }
    };

    /**
     * Step 3 – user picks a different financial supplier.
     * Resets amount to supplier minimum, clears period & installment.
     */
    const handleSupplierSelect = (supplier: ILoanTemplateDetailResponse) => {
        const minAmount = supplier.minAmount ?? 0;

        setState((prev) => ({
            ...prev,
            selectedLoanTemplateDetailItem: supplier,
            loanAmount: minAmount,
            refundPeriod: [],
            selectedRefundPeriod: undefined,
            installmentCalculated: undefined,
        }));

        const loanTemplateId = state.initializeParams.find((p) =>
            p.loanTemplateDetail.some(
                (d) => d.financialSupplierId === supplier.financialSupplierId
            )
        )?.loanTemplate.id;

        getRegistrationDurationMutation.mutate({
            loanAmount: minAmount,
            loanTemplateId,
            financialSupplierId: supplier.financialSupplierId,
        } as IGetRegistrationDurationRequest);
    };

    /**
     * Step 4 – user moves the slider.
     * Re-fetches the period options for the new amount; clears period & installment.
     */
    const handleLoanAmountChange = (amount: number) => {
        setState((prev) => ({
            ...prev,
            loanAmount: amount,
            refundPeriod: [],
            selectedRefundPeriod: undefined,
            installmentCalculated: undefined,
        }));

        if (!state.selectedLoanTemplateDetailItem) return;

        getRegistrationDurationMutation.mutate({
            loanAmount: amount,
            loanTemplateId: state.initializeParams.find((p) =>
                p.loanTemplateDetail.some(
                    (d) =>
                        d.financialSupplierId ===
                        state.selectedLoanTemplateDetailItem
                            ?.financialSupplierId
                )
            )?.loanTemplate.id,
            financialSupplierId:
                state.selectedLoanTemplateDetailItem.financialSupplierId,
        } as IGetRegistrationDurationRequest);
    };

    /**
     * Step 5 – user picks a repayment period.
     * Triggers the installment calculation.
     */
    const handleRefundPeriodSelect = (period: IDropDownResponse) => {
        setState((prev) => ({
            ...prev,
            selectedRefundPeriod: period,
            installmentCalculated: undefined,
        }));

        calculateLoanParamsMutation.mutate({
            loanAmount: state.loanAmount,
            loanNetAmount: 0,
            stepSpaceId: period.id,
            organizationId: state.selectedOrganization?.id ?? null,
            loanTemplateId: state.initializeParams.find((p) =>
                p.loanTemplateDetail.some(
                    (d) =>
                        d.financialSupplierId ===
                        state.selectedLoanTemplateDetailItem
                            ?.financialSupplierId
                )
            )?.loanTemplate.id,
            financialSupplier:
                state.selectedLoanTemplateDetailItem?.financialSupplier,
        } as ICalculateLoanParamsRequest);
    };

    /**
     * Step 7 – submit.
     */
    const handleRegisterLoan = () => {
        if (!state.selectedRefundPeriod) return;

        getTokenTime();
        const {token} = getJwtToken();

        if (!token) {
            dispatch(setUnAuthorizedState({state: `login ${UUID}`}));
            return;
        }

        registerLoanMutation.mutate({
            loanAmount: state.loanAmount,
            stepSpaceId: state.selectedRefundPeriod.id,
            organizationId: state.selectedOrganization?.id ?? null,
            loanTemplateId: state.initializeParams.find((p) =>
                p.loanTemplateDetail.some(
                    (d) =>
                        d.financialSupplierId ===
                        state.selectedLoanTemplateDetailItem
                            ?.financialSupplierId
                )
            )?.loanTemplate.id,
            financialSupplier:
                state.selectedLoanTemplateDetailItem?.financialSupplier,
        } as ICalculateLoanParamsRequest);
    };

    // ── Derived gate conditions (what is enabled at each step) ────────────

    // Organisation gate: if there are organisations the user must pick one first
    const organizationRequired = state.organizations.length > 0;
    const organizationGatePassed =
        !organizationRequired || state.selectedOrganization !== undefined;

    const supplierActive = organizationGatePassed;
    const sliderActive =
        supplierActive && state.selectedLoanTemplateDetailItem !== undefined;
    const periodActive = sliderActive && state.loanAmount > 0;
    const submitActive = state.installmentCalculated !== undefined;

    // ── Render ─────────────────────────────────────────────────────────────

    if (isLoading) return <CreateNewRequestSkeleton />;

    const {
        loanTemplates,
        organizations,
        loanTemplateDetail,
        selectedOrganization,
        selectedLoanTemplateDetailItem,
        loanAmount,
        refundPeriod,
        selectedRefundPeriod,
        installmentCalculated,
    } = state;

    return (
        <div
            style={{
                height: 'calc(-64px + 100svh)',
                paddingInline: '20px',
                direction: 'ltr',
                overflowY: 'auto',
                paddingTop: '15px',
                paddingBottom: '60px',
            }}
        >
            <Grid style={{overflowY: 'auto', direction: 'rtl'}}>
                {/* ── Card header ── */}
                <TemplateLoanCard
                    description={loanTemplates[0]?.description}
                    title={loanTemplates[0]?.title}
                    logo={loanTemplates[0]?.logoUrl}
                />

                {/* ── Step 2: Organisation (optional) ── */}
                {organizationRequired && (
                    <div style={{margin: 'auto', paddingTop: '20px'}}>
                        <div style={{paddingBottom: '10px'}}>
                            <span
                                className="text-sm font-normal primaryColorLight-900 anjomanRegular"
                                style={{fontSize: '14px'}}
                            >
                                نام سازمان
                            </span>
                        </div>
                        <CustomisedDropDown
                            title={selectedOrganization?.title ?? 'انتخاب کنید'}
                            options={organizations}
                            label="سازمان"
                            setSelectedItem={handleOrganizationSelect}
                            isSelected={selectedOrganization !== undefined}
                        />
                    </div>
                )}

                {/* ── Step 3: Financial supplier ── */}
                {loanTemplateDetail.length > 0 && (
                    <div
                        style={{
                            margin: 'auto',
                            paddingTop: '20px',
                            opacity: supplierActive ? 1 : 0.4,
                            pointerEvents: supplierActive ? 'auto' : 'none',
                        }}
                    >
                        <div style={{paddingBottom: '10px'}}>
                            <span
                                className="text-sm font-normal primaryColorLight-900 anjomanRegular"
                                style={{fontSize: '14px'}}
                            >
                                منبع تامین مالی
                            </span>
                        </div>
                        <SupplierSelect
                            options={loanTemplateDetail}
                            setSelectedItem={handleSupplierSelect}
                            selectedItem={selectedLoanTemplateDetailItem}
                            enableMode={supplierActive}
                        />
                    </div>
                )}

                {/* ── Step 4: Loan amount slider ── */}
                <div
                    style={{
                        margin: 'auto',
                        paddingTop: '20px',
                        opacity: sliderActive ? 1 : 0.4,
                        pointerEvents: sliderActive ? 'auto' : 'none',
                    }}
                >
                    <CustomisedSlider
                        minAmount={
                            selectedLoanTemplateDetailItem?.minAmount ?? 0
                        }
                        maxAmount={
                            selectedLoanTemplateDetailItem?.maxAmount ??
                            100_000_000
                        }
                        defaultValue={
                            selectedLoanTemplateDetailItem?.minAmount ?? 0
                        }
                        amountStep={
                            selectedLoanTemplateDetailItem?.amountStep ?? 10
                        }
                        setLoanAmount={handleLoanAmountChange}
                        enableMode={sliderActive}
                        selectedLoanTemplateDetailItem={
                            selectedLoanTemplateDetailItem
                        }
                        selectedLoanAmount={loanAmount}
                    />
                </div>

                {/* ── Step 5: Repayment period ── */}
                <div
                    style={{
                        margin: 'auto',
                        paddingTop: '20px',
                        opacity: periodActive ? 1 : 0.4,
                        pointerEvents: periodActive ? 'auto' : 'none',
                    }}
                >
                    <div style={{paddingBottom: '10px'}}>
                        <span
                            className="text-sm font-normal primaryColorLight-900 anjomanRegular"
                            style={{fontSize: '14px'}}
                        >
                            مدت بازپرداخت
                        </span>
                    </div>
                    <CustomisedDropDown
                        options={refundPeriod}
                        title={selectedRefundPeriod?.title ?? 'انتخاب کنید'}
                        label="مدت بازپرداخت"
                        setSelectedItem={handleRefundPeriodSelect}
                        isSelected={selectedRefundPeriod !== undefined}
                    />
                </div>

                {/* ── Step 6: Installment preview (read-only) ── */}
                <div
                    style={{
                        margin: 'auto',
                        paddingTop: '20px',
                        opacity: installmentCalculated ? 1 : 0.4,
                    }}
                >
                    <div style={{paddingBottom: '10px'}}>
                        <span
                            className="text-sm font-normal primaryColorLight-900 anjomanRegular"
                            style={{fontSize: '14px'}}
                        >
                            شرایط بازپرداخت
                        </span>
                    </div>
                </div>

                <div
                    className="installmentWrapper border border-[var(--border-color)]"
                    style={{opacity: installmentCalculated ? 1 : 0.4}}
                >
                    <div
                        className="gridContainer"
                        style={{paddingTop: '10px', paddingBottom: '15px'}}
                    >
                        {/* Loan amount */}
                        <div className="column">
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                <span
                                    className={`anjomanSemiBold font-bold text-sm ${installmentCalculated ? 'primaryColorLight-900' : 'primaryColorLight-100'}`}
                                    style={{fontSize: '10px'}}
                                >
                                    مبلغ تسهیلات
                                </span>
                                <span
                                    className="text-sm font-bold primaryColorLight-900 yekanBakhFaNum"
                                    style={{
                                        fontSize: '15px',
                                        paddingTop: '10px',
                                    }}
                                >
                                    {installmentCalculated
                                        ? `${installmentCalculated.loanAmount.toLocaleString()} ریال`
                                        : ''}
                                </span>
                            </div>
                        </div>

                        {/* Installment amount */}
                        <div className="column">
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                <span
                                    className={`anjomanSemiBold font-bold text-sm ${installmentCalculated ? 'primaryColorLight-900' : 'primaryColorLight-100'}`}
                                    style={{fontSize: '10px'}}
                                >
                                    مبلغ هر قسط
                                </span>
                                <span
                                    className="text-sm font-bold primaryColorLight-900 yekanBakhFaNum"
                                    style={{
                                        fontSize: '15px',
                                        paddingTop: '10px',
                                    }}
                                >
                                    {installmentCalculated
                                        ? `${installmentCalculated.installmentAmount.toLocaleString()} ریال`
                                        : ''}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </Grid>

            {/* ── Step 7: Submit ── */}
            <Grid item xs={12} className="py-5" style={{paddingTop: '0px'}}>
                <CustomisedButton
                    title="ادامه"
                    disable={!submitActive}
                    onClick={handleRegisterLoan}
                    type="submit"
                />
            </Grid>
        </div>
    );
};

export default CreateNewRequest;
